import type Phaser from 'phaser';
import { PORTRAIT_FRAME, PORTRAIT_HOLE, TEX, portraitKey } from './assets';
import { LOADER_TEX, ensureLoaderPaint } from './glenPaint';
import {
  GLEN_TIPS,
  LOADER_ENTRANCE_SEC,
  LOADER_PERCENT_SIZE,
  PORTRAIT_SPIN_MS,
  PORTRAIT_SPIN_REDUCED_MS,
  SIGN_SWAY_DEG,
  SIGN_SWAY_MS,
  SIGN_SWAY_REDUCED_DEG,
  loaderCritter,
  loaderStack,
  progressPercentLabel,
} from './glenLoadPlan';
import { rasterUrl } from './raster';
import { stage } from './stage';

/** Sideways wobble added to the rope rotation, in world pixels. */
export const SIGN_SWAY_X = 14;
export const SIGN_SWAY_REDUCED_X = 4;

const STYLE_ID = 'glen-loader-motion';
const ROOT_ID = 'glen-loader';

/**
 * How long a `?glenBlock=` stall may run. The capture uses this to prove the
 * CSS spin survives a blocked main thread. Production omits the parameter.
 */
export const GLEN_BLOCK_MAX_MS = 5000;

export function glenBlockMsFromLocation(search: string): number {
  const raw = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search).get('glenBlock');
  const ms = Number(raw);
  if (!Number.isFinite(ms) || ms <= 0) return 0;
  return Math.min(GLEN_BLOCK_MAX_MS, Math.round(ms));
}

/** Busy-wait. `performance.now` is wall time, so the stall is not a timer the browser can defer. */
export function blockMainThread(ms: number): void {
  if (ms <= 0) return;
  const end = performance.now() + ms;
  while (performance.now() < end) {
    // Intentionally empty. The compositor keeps the CSS transforms moving.
  }
}

export interface LoaderHang {
  plankW: number;
  plankH: number;
  restY: number;
  startY: number;
  cx: number;
  pivotY: number;
  drop: number;
  trackW: number;
  frameSize: number;
  hole: number;
  title: number;
  portrait: number;
  tip: number;
  track: number;
  percent: number;
}

/** Sign geometry in world pixels, matching the glen scene's camera scroll. */
export function loaderHang(gameW: number, gameH: number, scrollX: number, scrollY: number): LoaderHang & { scrollX: number; scrollY: number } {
  const width = Number.isFinite(gameW) ? gameW : stage.w;
  const height = Number.isFinite(gameH) ? gameH : stage.h;
  const stack = loaderStack();
  const plankW = Math.min(560, Math.round(width - 64));
  const restY = Math.round(Math.min(height * 0.54, height - stack.plankH / 2 - 16));
  const startY = Math.round(-stack.plankH * 0.65);
  return {
    plankW,
    plankH: stack.plankH,
    restY,
    startY,
    cx: width / 2,
    pivotY: scrollY,
    drop: startY - restY,
    trackW: plankW - 150,
    frameSize: stack.frameSize,
    hole: Math.round(stack.frameSize * (PORTRAIT_HOLE / PORTRAIT_FRAME)),
    title: stack.title,
    portrait: stack.portrait,
    tip: stack.tip,
    track: stack.track,
    percent: stack.percent,
    scrollX,
    scrollY,
  };
}

/**
 * Compositor-only motion. Every animated property is `transform`.
 * Width, top, and left never change inside a keyframe, so a blocked main
 * thread cannot freeze the spin or the sway.
 */
export function loaderOverlayCss(): string {
  const spin = PORTRAIT_SPIN_MS;
  const spinSlow = PORTRAIT_SPIN_REDUCED_MS;
  const sway = SIGN_SWAY_MS;
  const enter = Math.round(LOADER_ENTRANCE_SEC * 1000);
  return `
#${ROOT_ID} {
  position: fixed;
  z-index: 40;
  overflow: hidden;
  pointer-events: auto;
  opacity: 1;
  user-select: none;
  touch-action: none;
  transition: opacity 850ms linear;
}
#${ROOT_ID}.is-reduced { transition-duration: 280ms; }
#${ROOT_ID}.is-leaving { opacity: 0; pointer-events: none; }
#${ROOT_ID} .glen-world { position: absolute; left: 0; top: 0; }
#${ROOT_ID} .glen-pivot { position: absolute; width: 0; height: 0; }
#${ROOT_ID} .glen-slide {
  position: absolute;
  left: 0;
  top: 0;
  width: 0;
  height: 0;
  will-change: transform;
  animation: glen-loader-slide ${sway}ms ease-in-out infinite;
}
#${ROOT_ID}.is-reduced .glen-slide { animation-name: glen-loader-slide-reduced; }
#${ROOT_ID} .glen-sway {
  position: absolute;
  left: 0;
  top: 0;
  width: 0;
  height: 0;
  transform-origin: 50% 0;
  will-change: transform;
  animation: glen-loader-sway ${sway}ms ease-in-out infinite;
}
#${ROOT_ID}.is-reduced .glen-sway { animation-name: glen-loader-sway-reduced; }
#${ROOT_ID} .glen-drop {
  position: absolute;
  left: 0;
  top: 0;
  width: 0;
  height: 0;
  transform-origin: 50% 0;
  will-change: transform;
  animation: glen-loader-drop ${enter}ms cubic-bezier(0.16, 0.84, 0.24, 1) forwards;
}
#${ROOT_ID}.is-reduced .glen-drop { animation-duration: 160ms; }
#${ROOT_ID} .glen-sign { position: absolute; }
#${ROOT_ID} .glen-plank { position: absolute; inset: 0; width: 100%; height: 100%; pointer-events: none; }
#${ROOT_ID} .glen-title, #${ROOT_ID} .glen-tip, #${ROOT_ID} .glen-percent {
  position: absolute;
  left: 60px;
  right: 60px;
  text-align: center;
  font-family: Fredoka, sans-serif;
  font-weight: 700;
  color: #FFF6E4;
  paint-order: stroke fill;
  -webkit-text-stroke: 3px #3B2414;
  text-shadow: 0 2px 0 #3B2414;
}
#${ROOT_ID} .glen-title, #${ROOT_ID} .glen-percent {
  font-size: ${LOADER_PERCENT_SIZE};
  -webkit-text-stroke-width: 4px;
}
#${ROOT_ID} .glen-tip { font-size: 24px; }
#${ROOT_ID} .glen-medallion { position: absolute; }
#${ROOT_ID} .glen-spin {
  position: absolute;
  inset: 0;
  transform-origin: 50% 50%;
  will-change: transform;
  animation: glen-loader-spin ${spin}ms linear infinite;
}
#${ROOT_ID}.is-reduced .glen-spin { animation-duration: ${spinSlow}ms; }
#${ROOT_ID} .glen-face, #${ROOT_ID} .glen-frame { position: absolute; pointer-events: none; }
#${ROOT_ID} .glen-face { border-radius: 50%; }
#${ROOT_ID} .glen-groove {
  position: absolute;
  background: #2a160c;
  border-radius: 16px;
  box-shadow: inset 0 0 0 2px rgba(231, 201, 138, 0.85);
}
#${ROOT_ID} .glen-groove-inner {
  position: absolute;
  left: 6px;
  right: 6px;
  top: 5px;
  bottom: 5px;
  background: #4a2e1c;
  border-radius: 12px;
  overflow: hidden;
}
#${ROOT_ID} .glen-honey {
  position: absolute;
  left: 0;
  right: 0;
  top: 4px;
  height: 18px;
  transform-origin: left center;
  transform: scaleX(0);
  background: linear-gradient(#fff1b8, #f0c14a 45%, #d4842a);
  border-radius: 9px;
}
#${ROOT_ID} .glen-acorn {
  position: absolute;
  width: 34px;
  height: 34px;
  margin-left: -17px;
  margin-top: -17px;
  pointer-events: none;
  transform: translate3d(0, 0, 0);
}
#${ROOT_ID} .glen-ropes { position: absolute; left: 0; top: 0; overflow: visible; pointer-events: none; }
@keyframes glen-loader-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
@keyframes glen-loader-slide {
  0%, 100% { transform: translate3d(0, 0, 0); }
  25% { transform: translate3d(${SIGN_SWAY_X}px, 0, 0); }
  75% { transform: translate3d(${-SIGN_SWAY_X}px, 0, 0); }
}
@keyframes glen-loader-slide-reduced {
  0%, 100% { transform: translate3d(0, 0, 0); }
  25% { transform: translate3d(${SIGN_SWAY_REDUCED_X}px, 0, 0); }
  75% { transform: translate3d(${-SIGN_SWAY_REDUCED_X}px, 0, 0); }
}
@keyframes glen-loader-sway {
  0%, 100% { transform: translate3d(0, 0, 0) rotate(0deg); }
  25% { transform: translate3d(${SIGN_SWAY_X}px, 0, 0) rotate(${SIGN_SWAY_DEG}deg); }
  75% { transform: translate3d(${-SIGN_SWAY_X}px, 0, 0) rotate(${-SIGN_SWAY_DEG}deg); }
}
@keyframes glen-loader-sway-reduced {
  0%, 100% { transform: translate3d(0, 0, 0) rotate(0deg); }
  25% { transform: translate3d(${SIGN_SWAY_REDUCED_X}px, 0, 0) rotate(${SIGN_SWAY_REDUCED_DEG}deg); }
  75% { transform: translate3d(${-SIGN_SWAY_REDUCED_X}px, 0, 0) rotate(${-SIGN_SWAY_REDUCED_DEG}deg); }
}
@keyframes glen-loader-drop {
  from { transform: translate3d(0, var(--glen-drop), 0); }
  to { transform: translate3d(0, 0, 0); }
}
`;
}

export interface GlenLoaderOverlay {
  setShown(amount: number): void;
  setTip(text: string): void;
  fade(ms: number): void;
  releasePointer(): void;
  destroy(): void;
}

function installCss(): void {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = loaderOverlayCss();
  document.head.appendChild(style);
}

function sourceUrl(scene: Phaser.Scene, key: string, fallback: string): string {
  if (!scene.textures.exists(key)) return fallback;
  const source = scene.textures.get(key).getSourceImage() as CanvasImageSource & {
    src?: string;
    currentSrc?: string;
    naturalWidth?: number;
    naturalHeight?: number;
    width?: number;
    height?: number;
    toDataURL?: () => string;
  };
  try {
    const width = source.naturalWidth || source.width || 0;
    const height = source.naturalHeight || source.height || 0;
    if (width > 2 && height > 2 && typeof document !== 'undefined') {
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(source, 0, 0);
        return canvas.toDataURL();
      }
    }
    if (typeof source.toDataURL === 'function') return source.toDataURL();
  } catch {
    // A revoked blob or a tainted canvas falls through to the file.
  }
  const src = source.currentSrc || source.src;
  if (typeof src === 'string' && src.length > 0 && !src.startsWith('blob:')) return src;
  return fallback;
}

function cameraScroll(gameW: number, gameH: number): { x: number; y: number } {
  return {
    x: -Math.max(0, (gameW - stage.w) / 2),
    y: -Math.max(0, (gameH - stage.h) / 2),
  };
}

/**
 * The hanging sign, portrait, bar, and percent live above the canvas.
 * Spin and sway are CSS transform keyframes so they keep moving when the
 * main thread is inside texture decode or scene create.
 */
export function mountGlenLoader(scene: Phaser.Scene, reduced: boolean): GlenLoaderOverlay {
  installCss();
  document.getElementById(ROOT_ID)?.remove();

  const gameW = scene.scale.width;
  const gameH = scene.scale.height;
  const scroll = cameraScroll(gameW, gameH);
  const hang = loaderHang(gameW, gameH, scroll.x, scroll.y);
  const signKey = ensureLoaderPaint(scene, hang.plankW, hang.plankH);
  const critter = loaderCritter();
  const faceUrl = sourceUrl(
    scene,
    portraitKey(critter),
    rasterUrl(`assets/heroes/cc_hero_${critter}_portrait_round@2x.png`),
  );
  const frameUrl = sourceUrl(scene, TEX.portraitFrame, rasterUrl('assets/ui/cc_ui_portrait_frame_round@1x.png'));
  const acornKey = scene.textures.exists(TEX.topbarAcorn) ? TEX.topbarAcorn : LOADER_TEX.acorn;
  const acornUrl = sourceUrl(scene, acornKey, 'assets/ui/topbar/cc_topbar_icon_acorn.png');
  const signUrl = sourceUrl(scene, signKey, '');
  const honeyUrl = sourceUrl(scene, LOADER_TEX.honey, '');

  const root = document.createElement('div');
  root.id = ROOT_ID;
  root.dataset.glenLoader = 'open';
  if (reduced) root.classList.add('is-reduced');

  const world = document.createElement('div');
  world.className = 'glen-world';
  root.appendChild(world);

  const pivot = document.createElement('div');
  pivot.className = 'glen-pivot';
  world.appendChild(pivot);

  const drop = document.createElement('div');
  drop.className = 'glen-drop';
  drop.style.setProperty('--glen-drop', `${hang.drop}px`);
  pivot.appendChild(drop);

  const slide = document.createElement('div');
  slide.className = 'glen-slide';
  drop.appendChild(slide);

  const eyeY = hang.restY - hang.pivotY - hang.plankH / 2 + 28;
  const half = hang.plankW * 0.28;
  const ropes = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  ropes.setAttribute('class', 'glen-ropes');
  ropes.setAttribute('width', '1');
  ropes.setAttribute('height', '1');
  ropes.setAttribute('overflow', 'visible');
  const rope = (x: number, width: number, color: string) => {
    const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line.setAttribute('x1', String(x));
    line.setAttribute('y1', '0');
    line.setAttribute('x2', String(x));
    line.setAttribute('y2', String(eyeY));
    line.setAttribute('stroke', color);
    line.setAttribute('stroke-width', String(width));
    line.setAttribute('stroke-linecap', 'round');
    ropes.appendChild(line);
  };
  rope(-half, 7, '#3a2414');
  rope(half, 7, '#3a2414');
  rope(-half - 1, 3, '#c4a574');
  rope(half - 1, 3, '#c4a574');
  slide.appendChild(ropes);

  const sway = document.createElement('div');
  sway.className = 'glen-sway';
  sway.style.top = `${eyeY}px`;
  drop.appendChild(sway);

  const sign = document.createElement('div');
  sign.className = 'glen-sign';
  sign.style.width = `${hang.plankW}px`;
  sign.style.height = `${hang.plankH}px`;
  sign.style.left = `${-hang.plankW / 2}px`;
  sign.style.top = '-28px';
  sway.appendChild(sign);

  const plank = document.createElement('img');
  plank.className = 'glen-plank';
  plank.alt = '';
  plank.src = signUrl;
  sign.appendChild(plank);

  const title = document.createElement('div');
  title.className = 'glen-title';
  title.textContent = 'Entering the glen';
  title.style.top = `${hang.title}px`;
  title.style.transform = 'translateY(-50%)';
  sign.appendChild(title);

  const medallion = document.createElement('div');
  medallion.className = 'glen-medallion';
  medallion.style.width = `${hang.frameSize}px`;
  medallion.style.height = `${hang.frameSize}px`;
  medallion.style.left = '50%';
  medallion.style.top = `${hang.portrait}px`;
  medallion.style.marginLeft = `${-hang.frameSize / 2}px`;
  medallion.style.marginTop = `${-hang.frameSize / 2}px`;
  sign.appendChild(medallion);

  const spin = document.createElement('div');
  spin.className = 'glen-spin';
  medallion.appendChild(spin);

  const face = document.createElement('img');
  face.className = 'glen-face';
  face.alt = '';
  face.src = faceUrl;
  face.style.width = `${hang.hole}px`;
  face.style.height = `${hang.hole}px`;
  face.style.left = '50%';
  face.style.top = '50%';
  face.style.marginLeft = `${-hang.hole / 2}px`;
  face.style.marginTop = `${-hang.hole / 2}px`;
  spin.appendChild(face);

  const frame = document.createElement('img');
  frame.className = 'glen-frame';
  frame.alt = '';
  frame.src = frameUrl;
  frame.style.inset = '0';
  frame.style.width = '100%';
  frame.style.height = '100%';
  spin.appendChild(frame);

  const tip = document.createElement('div');
  tip.className = 'glen-tip';
  tip.textContent = GLEN_TIPS[0];
  tip.style.top = `${hang.tip}px`;
  sign.appendChild(tip);

  const groove = document.createElement('div');
  groove.className = 'glen-groove';
  const grooveW = hang.trackW + 20;
  groove.style.width = `${grooveW}px`;
  groove.style.height = '36px';
  groove.style.left = '50%';
  groove.style.top = `${hang.track - 16}px`;
  groove.style.marginLeft = `${-grooveW / 2}px`;
  sign.appendChild(groove);

  const inner = document.createElement('div');
  inner.className = 'glen-groove-inner';
  groove.appendChild(inner);

  const honey = document.createElement('div');
  honey.className = 'glen-honey';
  if (honeyUrl) {
    honey.style.backgroundImage = `url("${honeyUrl}")`;
    honey.style.backgroundSize = '100% 100%';
    honey.style.backgroundRepeat = 'no-repeat';
  }
  inner.appendChild(honey);

  const acorn = document.createElement('img');
  acorn.className = 'glen-acorn';
  acorn.alt = '';
  acorn.src = acornUrl;
  acorn.style.left = `${(hang.plankW - hang.trackW) / 2}px`;
  acorn.style.top = `${hang.track - 2}px`;
  sign.appendChild(acorn);

  const percent = document.createElement('div');
  percent.className = 'glen-percent';
  percent.dataset.glenPercent = '0%';
  percent.textContent = '0%';
  percent.style.top = `${hang.percent}px`;
  percent.style.transform = 'translateY(-50%)';
  sign.appendChild(percent);

  const place = () => {
    const canvas = scene.game.canvas;
    const rect = canvas.getBoundingClientRect();
    const scale = gameW > 0 && rect.width > 0 ? rect.width / gameW : 1;
    root.style.left = `${rect.left}px`;
    root.style.top = `${rect.top}px`;
    root.style.width = `${rect.width}px`;
    root.style.height = `${rect.height}px`;
    world.style.width = `${gameW}px`;
    world.style.height = `${gameH}px`;
    world.style.transform = `translate(${-scroll.x * scale}px, ${-scroll.y * scale}px) scale(${scale})`;
    world.style.transformOrigin = '0 0';
    pivot.style.left = `${hang.cx}px`;
    pivot.style.top = `${hang.pivotY}px`;
  };
  place();
  document.body.appendChild(root);
  const onResize = () => place();
  window.addEventListener('resize', onResize);
  scene.scale.on('resize', onResize);

  let gone = false;
  const destroy = () => {
    if (gone) return;
    gone = true;
    window.removeEventListener('resize', onResize);
    scene.scale.off('resize', onResize);
    root.remove();
  };

  return {
    setShown(amount: number) {
      const shown = Number.isFinite(amount) ? Math.min(1, Math.max(0, amount)) : 0;
      const label = progressPercentLabel(shown);
      honey.style.transform = `scaleX(${shown})`;
      acorn.style.transform = `translate3d(${shown * hang.trackW}px, 0, 0)`;
      if (percent.textContent !== label) {
        percent.textContent = label;
        percent.dataset.glenPercent = label;
      }
    },
    setTip(text: string) {
      if (tip.textContent !== text) tip.textContent = text;
    },
    fade(ms: number) {
      root.dataset.glenLoader = 'fading';
      root.style.transitionDuration = `${Math.max(0, ms)}ms`;
      root.classList.add('is-leaving');
    },
    releasePointer() {
      root.style.pointerEvents = 'none';
    },
    destroy,
  };
}
